import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    ChevronDown,
    Search,
    PenTool,
    GraduationCap,
    Users,
    Rocket,
    GaugeCircle,
    MonitorSmartphone,
    Building2,
    Factory,
    Check,
} from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/site/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

const COPY = {
    fr: {
        eyebrow: "Programmes d'appui & Capacity Building",
        h1: "Conception et mise en œuvre de programmes d'appui à l'internationalisation",
        subtitle: "De l'ingénierie du programme à la formation, au coaching et au suivi des résultats.",
        stats: [
            { value: "15+", label: "structures d'appui & BSO" },
            { value: "300+", label: "PME accompagnées" },
            { value: "15+", label: "pays d'intervention" },
        ],
        ctaPrimary: "Réserver un échange de 30 min avec Saber",
        ctaSecondary: "Écrire à WEYB",
        force: {
            index: "01",
            eyebrow: "Positionnement",
            title: "Une force de proposition, pas seulement d'exécution.",
            text: "WEYB n'intervient pas uniquement sur un cahier des charges existant. Nous identifions le besoin, le transformons en programme structuré, proposons la méthodologie, concevons les outils et les parcours, intégrons le digital, définissons les KPI, mettons en œuvre, mesurons les résultats et recommandons les ajustements.",
            verbs: ["Concevoir", "Former", "Accompagner", "Mettre en œuvre", "Mesurer"],
        },
        cycle: {
            index: "02",
            eyebrow: "Cycle d'intervention",
            title: "Tout ou partie du cycle d'un programme.",
            steps: [
                { num: "01", title: "Analyser", desc: "Comprendre les objectifs, les bénéficiaires et les besoins." },
                { num: "02", title: "Concevoir", desc: "Construire le programme, la méthodologie, les outils et les KPI." },
                { num: "03", title: "Former", desc: "Former les conseillers, formateurs et entreprises." },
                { num: "04", title: "Accompagner", desc: "Coaching, mise en pratique et accompagnement terrain." },
                { num: "05", title: "Déployer", desc: "Mettre en œuvre et coordonner le programme." },
                { num: "06", title: "Mesurer", desc: "Monitoring, évaluation, reporting et capitalisation." },
            ],
        },
        design: {
            index: "03",
            eyebrow: "Program design",
            title: "Conception de programmes d'appui à l'export.",
            text: "WEYB transforme les objectifs d'une structure d'appui ou d'un bailleur en dispositif opérationnel, avec ses composantes sur mesure :",
            items: [
                "Définition des objectifs", "Segmentation des entreprises", "Critères de sélection", "Diagnostic initial",
                "Parcours pédagogique", "Ateliers collectifs", "Coaching individuel", "Outils digitaux & e-learning",
                "Missions terrain", "Activités B2B", "Salons internationaux", "Suivi des plans d'action",
                "Indicateurs de performance", "Reporting", "Évaluation finale",
            ],
            img: "/assets/terrain-formation-groupe.jpg",
            imgAlt: "Session de formation collective — programme WEYB",
        },
        tot: {
            index: "04",
            eyebrow: "Training of Trainers",
            title: "Formation de formateurs et conseillers.",
            text: "WEYB forme consultants, coachs, conseillers export, formateurs et équipes des structures d'appui, pour leur permettre de déployer en autonomie une méthodologie d'accompagnement des entreprises à l'international.",
            items: [
                "Appropriation de la méthodologie", "Outils de diagnostic et de stratégie export", "Animation d'ateliers",
                "Techniques de coaching", "Études de marché et prospection", "Suivi des entreprises",
                "Utilisation des outils digitaux", "Évaluation des acquis", "Accompagnement pendant le déploiement",
            ],
        },
        formation: {
            index: "05",
            eyebrow: "Formation des entreprises",
            title: "Former par l'action — learning by doing.",
            text: "Chaque participant travaille progressivement sur son propre projet export et repart avec des outils et des actions concrètes.",
            themes: [
                "Diagnostic export", "Stratégie export", "Sélection & priorisation des marchés", "Études de marché",
                "Proposition de valeur", "Business model international", "Prospection B2B", "LinkedIn & prospection digitale",
                "Prise de contact & qualification", "Rendez-vous B2B", "Négociation & closing commercial",
                "Préparation aux salons internationaux", "Missions B2B", "Développement commercial international",
            ],
            img: "/assets/terrain-workshop-agadir.jpg",
            imgAlt: "Atelier de travail avec des entreprises — mission WEYB à Agadir",
        },
        coaching: {
            index: "06",
            eyebrow: "Coaching des entreprises",
            title: "La formation ne suffit pas : la continuité fait la différence.",
            flow: ["Formation", "Application", "Coaching", "Exécution", "Suivi"],
            items: [
                "Coaching individuel et collectif", "E-coaching", "Suivi du plan d'action",
                "Accompagnement à la prospection", "Préparation des rendez-vous et débriefing",
                "Accompagnement salons & missions", "Suivi des résultats",
            ],
        },
        digital: {
            index: "07",
            eyebrow: "Digitalisation des programmes",
            title: "Le digital au service du parcours, jamais l'inverse.",
            text: "Export360, la plateforme développée par WEYB, peut être intégrée aux programmes lorsque c'est pertinent : diagnostic, e-learning, outils et templates, suivi des participants, quiz, leaderboard, études assistées par IA et pilotage. Elle n'est jamais obligatoire — c'est un accélérateur, pas une condition.",
        },
        me: {
            index: "08",
            eyebrow: "Monitoring, Evaluation & Learning",
            title: "Mesurer ce qui compte, capitaliser ce qui marche.",
            text: "WEYB contribue au suivi et à l'évaluation de programmes liés au développement export, à l'internationalisation des PME, au renforcement des capacités et à l'accès aux marchés.",
            items: [
                "Définition des indicateurs et baseline", "Suivi des KPI et collecte des données",
                "Suivi de participation et mesure de progression", "Suivi des résultats export",
                "Analyse qualitative et feedback des bénéficiaires", "Évaluations intermédiaire et finale",
                "Reporting", "Enseignements, recommandations et capitalisation",
            ],
        },
        pourQui: {
            index: "09",
            eyebrow: "Pour qui",
            title: "Deux niveaux d'intervention, une même exigence.",
            a: {
                title: "Structures d'appui & partenaires du développement",
                items: ["Bailleurs de fonds", "Agences de coopération", "Agences de promotion des exportations", "Chambres de commerce", "Fédérations & associations professionnelles", "Institutions publiques", "Programmes de développement", "Structures d'appui aux PME"],
                note: "WEYB intervient comme expert, concepteur de programme, formateur de formateurs, coach, opérateur de mise en œuvre, coordinateur ou expert M&E en développement export.",
            },
            b: {
                title: "Entreprises",
                items: ["Dirigeants", "Responsables export & commerciaux", "Équipes export", "PME & startups", "Entreprises primo-exportatrices", "Entreprises déjà présentes à l'international"],
                note: "Dans le cadre de programmes ou en direct : formation, coaching, diagnostic, conseil, prospection, préparation aux marchés, salons et missions B2B, suivi de plans d'action export.",
            },
        },
        gallery: {
            eyebrow: "Terrain",
            title: "Programmes et formations, en images.",
        },
        faq: {
            index: "10",
            eyebrow: "Questions fréquentes",
            items: [
                {
                    q: "WEYB peut-il concevoir un programme complet à partir d'un simple objectif ?",
                    a: "Oui. C'est précisément notre positionnement de force de proposition : nous partons de vos objectifs et contraintes pour concevoir le dispositif — méthodologie, parcours, outils, modalités pédagogiques, KPI — puis nous le mettons en œuvre et en mesurons les résultats.",
                },
                {
                    q: "Intervenez-vous uniquement en mise en œuvre, ou aussi en amont ?",
                    a: "Aux deux niveaux. L'intervention peut couvrir tout ou partie du cycle : diagnostic des besoins, conception, ingénierie pédagogique, formation de formateurs, formation et coaching des entreprises, mise en œuvre, suivi des bénéficiaires, monitoring & évaluation, capitalisation.",
                },
                {
                    q: "Export360 est-elle obligatoire dans vos programmes ?",
                    a: "Non. Export360 est un outil que WEYB peut intégrer lorsque c'est pertinent pour digitaliser le parcours (diagnostic, e-learning, suivi, pilotage). Chaque programme est conçu selon vos besoins, avec ou sans la plateforme.",
                },
                {
                    q: "Comment démarrer une collaboration sur un programme ?",
                    a: "Par un échange de 30 minutes avec Saber Bellaaj pour comprendre vos objectifs, vos bénéficiaires cibles et votre calendrier — puis une proposition structurée vous est soumise.",
                },
            ],
        },
        finalCta: {
            title: "Un programme à concevoir ou à déployer ?",
            text: "Échangeons sur vos objectifs, vos bénéficiaires et le dispositif adapté.",
            ctaPrimary: "Réserver un échange de 30 min avec Saber",
            ctaSecondary: "Découvrir nos solutions",
        },
    },
    en: {
        eyebrow: "Support Programs & Capacity Building",
        h1: "Design and implementation of internationalisation support programmes",
        subtitle: "From programme engineering to training, coaching and results monitoring.",
        stats: [
            { value: "15+", label: "support organisations & BSOs" },
            { value: "300+", label: "SMEs supported" },
            { value: "15+", label: "countries of operation" },
        ],
        ctaPrimary: "Book a 30-min call with Saber",
        ctaSecondary: "Write to WEYB",
        force: {
            index: "01",
            eyebrow: "Positioning",
            title: "A proactive partner, not just an implementer.",
            text: "WEYB does not only work from an existing terms of reference. We identify the need, turn it into a structured programme, propose the methodology, design tools and learning paths, integrate digital, define KPIs, implement, measure results and recommend adjustments.",
            verbs: ["Design", "Train", "Support", "Implement", "Measure"],
        },
        cycle: {
            index: "02",
            eyebrow: "Intervention cycle",
            title: "All or part of a programme cycle.",
            steps: [
                { num: "01", title: "Analyse", desc: "Understand objectives, beneficiaries and needs." },
                { num: "02", title: "Design", desc: "Build the programme, methodology, tools and KPIs." },
                { num: "03", title: "Train", desc: "Train advisors, trainers and companies." },
                { num: "04", title: "Support", desc: "Coaching, hands-on practice and field support." },
                { num: "05", title: "Deploy", desc: "Implement and coordinate the programme." },
                { num: "06", title: "Measure", desc: "Monitoring, evaluation, reporting and capitalisation." },
            ],
        },
        design: {
            index: "03",
            eyebrow: "Programme design",
            title: "Designing export support programmes.",
            text: "WEYB turns the objectives of a support organisation or a donor into an operational scheme, with tailor-made components:",
            items: [
                "Objective setting", "Company segmentation", "Selection criteria", "Initial diagnosis",
                "Learning path", "Collective workshops", "Individual coaching", "Digital tools & e-learning",
                "Field missions", "B2B activities", "International trade fairs", "Action plan follow-up",
                "Performance indicators", "Reporting", "Final evaluation",
            ],
            img: "/assets/terrain-formation-groupe.jpg",
            imgAlt: "Collective training session — WEYB programme",
        },
        tot: {
            index: "04",
            eyebrow: "Training of Trainers",
            title: "Training trainers and advisors.",
            text: "WEYB trains consultants, coaches, export advisors, trainers and support organisation teams, enabling them to autonomously deploy a methodology for supporting companies internationally.",
            items: [
                "Methodology appropriation", "Diagnosis and export strategy tools", "Workshop facilitation",
                "Coaching techniques", "Market studies and prospecting", "Company follow-up",
                "Digital tools usage", "Learning assessment", "Support during rollout",
            ],
        },
        formation: {
            index: "05",
            eyebrow: "Training for companies",
            title: "Learning by doing.",
            text: "Each participant progressively works on their own export project and leaves with concrete tools and actions.",
            themes: [
                "Export diagnosis", "Export strategy", "Market selection & prioritisation", "Market studies",
                "Value proposition", "International business model", "B2B prospecting", "LinkedIn & digital prospecting",
                "Outreach & qualification", "B2B meetings", "Negotiation & commercial closing",
                "Trade fair preparation", "B2B missions", "International business development",
            ],
            img: "/assets/terrain-workshop-agadir.jpg",
            imgAlt: "Working session with companies — WEYB mission in Agadir",
        },
        coaching: {
            index: "06",
            eyebrow: "Business coaching",
            title: "Training alone is not enough: continuity makes the difference.",
            flow: ["Training", "Application", "Coaching", "Execution", "Follow-up"],
            items: [
                "Individual and collective coaching", "E-coaching", "Action plan follow-up",
                "Prospecting support", "Meeting preparation and debriefing",
                "Trade fair & mission support", "Results monitoring",
            ],
        },
        digital: {
            index: "07",
            eyebrow: "Programme digitalisation",
            title: "Digital serves the journey, never the other way around.",
            text: "Export360, the platform developed by WEYB, can be embedded into programmes when relevant: diagnostics, e-learning, tools and templates, participant tracking, quizzes, leaderboard, AI-assisted studies and management. It is never mandatory — an accelerator, not a prerequisite.",
        },
        me: {
            index: "08",
            eyebrow: "Monitoring, Evaluation & Learning",
            title: "Measure what matters, capitalise on what works.",
            text: "WEYB contributes to monitoring and evaluating programmes related to export development, SME internationalisation, capacity building and market access.",
            items: [
                "Indicator definition and baseline", "KPI tracking and data collection",
                "Participation tracking and progress measurement", "Export results monitoring",
                "Qualitative analysis and beneficiary feedback", "Interim and final evaluations",
                "Reporting", "Lessons learned, recommendations and capitalisation",
            ],
        },
        pourQui: {
            index: "09",
            eyebrow: "Who for",
            title: "Two levels of intervention, the same standards.",
            a: {
                title: "Support organisations & development partners",
                items: ["Donors", "Cooperation agencies", "Export promotion agencies", "Chambers of commerce", "Federations & professional associations", "Public institutions", "Development programmes", "SME support organisations"],
                note: "WEYB intervenes as expert, programme designer, trainer of trainers, coach, implementation operator, coordinator or M&E expert in export development.",
            },
            b: {
                title: "Companies",
                items: ["Executives", "Export & sales managers", "Export teams", "SMEs & startups", "First-time exporters", "Companies already active internationally"],
                note: "Within programmes or directly: training, coaching, diagnosis, consulting, prospecting, market preparation, trade fairs and B2B missions, export action plan follow-up.",
            },
        },
        gallery: {
            eyebrow: "In the field",
            title: "Programmes and training, in pictures.",
        },
        faq: {
            index: "10",
            eyebrow: "Frequently asked questions",
            items: [
                {
                    q: "Can WEYB design a complete programme from a simple objective?",
                    a: "Yes. That is precisely our proactive positioning: we start from your objectives and constraints to design the scheme — methodology, learning paths, tools, teaching formats, KPIs — then implement it and measure results.",
                },
                {
                    q: "Do you only intervene in implementation, or also upstream?",
                    a: "Both. The intervention can cover all or part of the cycle: needs diagnosis, design, pedagogical engineering, training of trainers, business training and coaching, implementation, beneficiary follow-up, monitoring & evaluation, capitalisation.",
                },
                {
                    q: "Is Export360 mandatory in your programmes?",
                    a: "No. Export360 is a tool WEYB can integrate when relevant to digitalise the journey (diagnostics, e-learning, tracking, management). Each programme is designed around your needs, with or without the platform.",
                },
                {
                    q: "How do we start collaborating on a programme?",
                    a: "With a 30-minute call with Saber Bellaaj to understand your objectives, target beneficiaries and timeline — followed by a structured proposal.",
                },
            ],
        },
        finalCta: {
            title: "A programme to design or deploy?",
            text: "Let's discuss your objectives, your beneficiaries and the right scheme.",
            ctaPrimary: "Book a 30-min call with Saber",
            ctaSecondary: "Discover our solutions",
        },
    },
};

const CYCLE_ICONS = [Search, PenTool, GraduationCap, Users, Rocket, GaugeCircle];
const GALLERY = [
    { src: "/assets/terrain-formation.jpg", alt: { fr: "Formation en salle — programme WEYB", en: "Classroom training — WEYB programme" } },
    { src: "/assets/terrain-conference.jpg", alt: { fr: "Intervention en conférence — partage d'expérience terrain", en: "Conference talk — field experience sharing" } },
    { src: "/assets/terrain-startup-village.jpg", alt: { fr: "Écosystème Startup Village", en: "Startup Village ecosystem" } },
    { src: "/assets/terrain-matinale-export.jpg", alt: { fr: "Matinale de l'Export — Tunis", en: "Export Morning event — Tunis" } },
];

function FaqItem({ item, open, onToggle, index }) {
    return (
        <div className="border-b border-gray-200" data-testid={`programmes-faq-item-${index}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid={`programmes-faq-toggle-${index}`}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
            >
                <span className="font-display text-base md:text-lg font-medium text-weyb-ink group-hover:text-weyb-yellow-dark transition-colors">
                    {item.q}
                </span>
                <ChevronDown size={18} className={`shrink-0 text-weyb-yellow transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                    >
                        <p className="pb-6 text-sm md:text-base leading-relaxed text-gray-600 max-w-3xl">{item.a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function Programmes() {
    usePageMeta(
        { fr: "Programmes d'appui & Capacity Building — WEYB", en: "Support Programs & Capacity Building — WEYB" },
        { fr: "Conception et mise en œuvre de programmes d'appui à l'internationalisation : program design, formation de formateurs (ToT), formation & coaching des entreprises, monitoring & évaluation.", en: "Design and implementation of internationalisation support programmes: programme design, training of trainers (ToT), business training & coaching, monitoring & evaluation." }
    );
    const { lang, prefix } = useLang();
    const c = COPY[lang] || COPY.fr;
    const [openFaq, setOpenFaq] = useState(0);

    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faq.items.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };
    const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://weyb.fr/" },
            { "@type": "ListItem", position: 2, name: "Solutions", item: "https://weyb.fr/solutions" },
            { "@type": "ListItem", position: 3, name: lang === "en" ? "Support Programs & Capacity Building" : "Programmes d'appui & Capacity Building" },
        ],
    };
    const serviceLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: lang === "en" ? "Support Programs & Capacity Building — WEYB" : "Programmes d'appui & Capacity Building — WEYB",
        provider: { "@type": "Organization", name: "WEYB", url: "https://weyb.fr" },
        description: c.subtitle,
        areaServed: ["Europe", "Africa", "MENA"],
    };

    return (
        <div data-testid="programmes-page">
            <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
            <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
            <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

            {/* Hero */}
            <section className="pt-32 md:pt-44 pb-20 md:pb-28 bg-weyb-ink text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="P" dark>{c.eyebrow}</Eyebrow>
                    <Reveal>
                        <h1 data-testid="programmes-h1" className="font-display text-3xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-4xl">
                            {c.h1}
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">{c.subtitle}</p>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <div className="mt-10 flex flex-wrap gap-8 md:gap-12" data-testid="programmes-stats">
                            {c.stats.map((s) => (
                                <div key={s.label} className="flex items-baseline gap-3">
                                    <span className="font-display text-4xl md:text-5xl font-light text-weyb-yellow">{s.value}</span>
                                    <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/50 max-w-[130px]">{s.label}</span>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.26} className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="programmes-cta-booking"
                            className="group inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                        >
                            {c.ctaPrimary}
                            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <Link
                            to={`${prefix}/contact`}
                            data-testid="programmes-cta-contact"
                            className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-7 py-4 rounded-sm text-sm font-medium transition-colors"
                        >
                            {c.ctaSecondary}
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Force de proposition */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-force">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.force.index}>{c.force.eyebrow}</Eyebrow>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">{c.force.title}</h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="text-base text-gray-600 leading-relaxed">{c.force.text}</p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            {c.force.verbs.map((v, i) => (
                                <span key={v} data-testid={`programmes-verb-${i}`} className="font-display text-sm md:text-base font-semibold uppercase tracking-[0.1em] text-weyb-ink border border-gray-200 hover:border-weyb-yellow px-5 py-3 rounded-sm transition-colors">
                                    {v}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Cycle d'intervention */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100" data-testid="programmes-cycle">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.cycle.index}>{c.cycle.eyebrow}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink max-w-2xl leading-tight">{c.cycle.title}</h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                        {c.cycle.steps.map((s, i) => {
                            const Icon = CYCLE_ICONS[i];
                            return (
                                <Reveal key={s.num} delay={i * 0.06} className="h-full">
                                    <div data-testid={`programmes-cycle-step-${s.num}`} className="group h-full bg-white p-8 hover:bg-weyb-ink transition-colors duration-300">
                                        <div className="flex items-baseline justify-between">
                                            <span className="font-display text-4xl font-light text-weyb-yellow">{s.num}</span>
                                            <Icon size={20} strokeWidth={1.5} className="text-gray-300 group-hover:text-weyb-yellow transition-colors" aria-hidden="true" />
                                        </div>
                                        <h3 className="mt-6 font-display text-lg font-semibold uppercase tracking-[0.08em] text-weyb-ink group-hover:text-white transition-colors">{s.title}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-gray-600 group-hover:text-white/60 transition-colors">{s.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Program design */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-design">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div>
                        <Eyebrow index={c.design.index}>{c.design.eyebrow}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">{c.design.title}</h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-5 text-base text-gray-600 leading-relaxed">{c.design.text}</p>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <div className="mt-7 flex flex-wrap gap-2.5">
                                {c.design.items.map((item) => (
                                    <span key={item} className="text-xs font-medium text-gray-600 border border-gray-200 hover:border-weyb-yellow px-3.5 py-2 rounded-sm transition-colors">
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.12}>
                        <div className="relative">
                            <span className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-weyb-yellow" aria-hidden="true" />
                            <img src={c.design.img} alt={c.design.imgAlt} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-sm" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ToT */}
            <section className="py-20 md:py-28 bg-weyb-ink text-white" data-testid="programmes-tot">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.tot.index} dark>{c.tot.eyebrow}</Eyebrow>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                        <div>
                            <Reveal>
                                <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight">{c.tot.title}</h2>
                            </Reveal>
                            <Reveal delay={0.08}>
                                <p className="mt-5 text-base text-white/60 leading-relaxed">{c.tot.text}</p>
                            </Reveal>
                        </div>
                        <Reveal delay={0.12}>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                                {c.tot.items.map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm text-white/75">
                                        <Check size={15} className="mt-0.5 shrink-0 text-weyb-yellow" aria-hidden="true" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Formation entreprises */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-formation">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <Reveal delay={0.12} className="order-2 lg:order-1">
                        <div className="relative">
                            <span className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-weyb-yellow" aria-hidden="true" />
                            <img src={c.formation.img} alt={c.formation.imgAlt} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-sm" />
                        </div>
                    </Reveal>
                    <div className="order-1 lg:order-2">
                        <Eyebrow index={c.formation.index}>{c.formation.eyebrow}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">{c.formation.title}</h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-5 text-base text-gray-600 leading-relaxed">{c.formation.text}</p>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <div className="mt-7 flex flex-wrap gap-2.5">
                                {c.formation.themes.map((th) => (
                                    <span key={th} className="text-xs font-medium text-gray-600 border border-gray-200 hover:border-weyb-yellow px-3.5 py-2 rounded-sm transition-colors">
                                        {th}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Coaching */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100" data-testid="programmes-coaching">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.coaching.index}>{c.coaching.eyebrow}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink max-w-3xl leading-tight">{c.coaching.title}</h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-10 flex flex-wrap items-center gap-y-3" data-testid="programmes-coaching-flow">
                            {c.coaching.flow.map((f, i) => (
                                <span key={f} className="flex items-center">
                                    <span className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-weyb-ink bg-white border border-gray-200 px-5 py-3 rounded-sm">{f}</span>
                                    {i < c.coaching.flow.length - 1 && <ArrowRight size={16} className="mx-2 text-weyb-yellow" aria-hidden="true" />}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 max-w-4xl">
                            {c.coaching.items.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm text-gray-600">
                                    <Check size={15} className="mt-0.5 shrink-0 text-weyb-yellow" aria-hidden="true" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* Digitalisation */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-digital">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Eyebrow index={c.digital.index}>{c.digital.eyebrow}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">{c.digital.title}</h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-5 text-base text-gray-600 leading-relaxed max-w-2xl">{c.digital.text}</p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.12} className="lg:col-span-5">
                        <div className="bg-weyb-ink text-white rounded-sm p-8">
                            <MonitorSmartphone size={22} strokeWidth={1.5} className="text-weyb-yellow" aria-hidden="true" />
                            <p className="mt-4 font-display text-xl font-medium">Export360</p>
                            <Link
                                to={`${prefix}/solutions/export360`}
                                data-testid="programmes-export360-link"
                                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-weyb-yellow hover:text-weyb-yellow-dark transition-colors"
                            >
                                {lang === "en" ? "Discover the platform" : "Découvrir la plateforme"}
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* M&E */}
            <section className="py-20 md:py-28 bg-weyb-ink text-white" data-testid="programmes-me">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.me.index} dark>{c.me.eyebrow}</Eyebrow>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                        <div>
                            <Reveal>
                                <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight">{c.me.title}</h2>
                            </Reveal>
                            <Reveal delay={0.08}>
                                <p className="mt-5 text-base text-white/60 leading-relaxed">{c.me.text}</p>
                            </Reveal>
                        </div>
                        <Reveal delay={0.12}>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                                {c.me.items.map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm text-white/75">
                                        <Check size={15} className="mt-0.5 shrink-0 text-weyb-yellow" aria-hidden="true" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Pour qui */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-pourqui">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.pourQui.index}>{c.pourQui.eyebrow}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink max-w-2xl leading-tight">{c.pourQui.title}</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                        {[{ key: "a", data: c.pourQui.a, Icon: Building2 }, { key: "b", data: c.pourQui.b, Icon: Factory }].map(({ key, data, Icon }) => (
                            <Reveal key={key} className="h-full">
                                <div data-testid={`programmes-pourqui-${key}`} className="h-full border border-gray-200 rounded-sm p-8 md:p-10 hover:border-weyb-yellow transition-colors">
                                    <Icon size={22} strokeWidth={1.5} className="text-weyb-yellow" aria-hidden="true" />
                                    <h3 className="mt-5 font-display text-xl md:text-2xl font-medium text-weyb-ink tracking-tight">{data.title}</h3>
                                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                        {data.items.map((item) => (
                                            <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                                                <span className="mt-[7px] h-1 w-1 shrink-0 bg-weyb-yellow" aria-hidden="true" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-6 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">{data.note}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Galerie terrain */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100" data-testid="programmes-gallery">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="T">{c.gallery.eyebrow}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">{c.gallery.title}</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {GALLERY.map((g, i) => (
                            <Reveal key={g.src} delay={i * 0.06}>
                                <figure className="group relative overflow-hidden rounded-sm aspect-[4/3]">
                                    <img
                                        src={g.src}
                                        alt={g.alt[lang] || g.alt.fr}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 md:py-28 bg-white" data-testid="programmes-faq">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={c.faq.index}>{c.faq.eyebrow}</Eyebrow>
                    <div className="max-w-3xl">
                        {c.faq.items.map((item, i) => (
                            <FaqItem key={i} item={item} index={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA final */}
            <section className="py-24 md:py-36 bg-weyb-ink text-white" data-testid="programmes-final-cta">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-3xl">{c.finalCta.title}</h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-white/60 text-base md:text-lg max-w-xl leading-relaxed">{c.finalCta.text}</p>
                    </Reveal>
                    <Reveal delay={0.18} className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="programmes-final-cta-booking"
                            className="group inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-8 py-4 rounded-sm text-sm font-semibold transition-colors"
                        >
                            {c.finalCta.ctaPrimary}
                            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <Link
                            to={`${prefix}/solutions`}
                            data-testid="programmes-final-cta-solutions"
                            className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-8 py-4 rounded-sm text-sm font-medium transition-colors"
                        >
                            {c.finalCta.ctaSecondary}
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
